import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { Observable, Subject, catchError, from, of, retry, tap } from 'rxjs';
import { EventDTO } from '../api/models';
import { BulgariSnackbarComponent } from '../components/bulgari-snackbar/bulgari-snackbar.component';
import { DeleteDialogComponent } from '../components/dialogs/delete-dialog/delete-dialog.component';
import { UploadDialogComponent } from '../components/dialogs/upload-dialog/upload-dialog.component';
import { AppointmentStatus } from '../enums/appointment-status-enum';
import { ProductStatus } from '../enums/product-status-enum';
import { TranslateService } from '@ngx-translate/core';
import { RoleEnum } from '../enums/role-enum';
import { environment } from '../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { FileExportPayload } from '../enums/shared.type';
import { ExportFormat, Section } from '../enums/shared.enum';
import { DashboardService } from '../api/services/dashboard.service';
import { AllocationService, LogisticService, ManagementService } from '../api/services';
import { TraysService } from '../api/services/trays.service';
import { AnalyticsService } from '../api/services/analytics.service';
import { Router } from '@angular/router';
import { RouteName, ROUTES } from '../enums/route.constants';
import { FinanceService } from '../api/services/finance.service';

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  loaderListener = new Subject<boolean>();
  roleListener = new Subject<RoleEnum>();
  loaderTriggered = this.loaderListener.asObservable();
  roleTriggered = this.roleListener.asObservable();
  userLogged: any;

  LIMIT_DASHBOARD = 50;
  LIMIT_PRODUCT = 50;
  LIMIT_WATCH = 50;
  LIMIT_SHOWCASES = 8;
  LIMIT_BOOKED_FOR_SALES = 50;

  public readonly EVENT_DAY_OFFSET = 2;

  private _userRole?: RoleEnum;
  public get userRole() {
    return this._userRole;
  }

  private _event!: EventDTO;
  public get event(): EventDTO {
    return this._event;
  }

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private msalService: MsalService,
    private readonly dashboardService: DashboardService,
    private readonly managementService: ManagementService,
    private readonly allocationService: AllocationService,
    private readonly traysService: TraysService,
    private readonly logisticService: LogisticService,
    private readonly analyticsService: AnalyticsService,
    private readonly financeService: FinanceService,
    private readonly router: Router,
  ) {
    let event = this.getEvent();
    if (event) {
      this._event = event;
    } else {
      this.goToEventPage();
    }
  }

  setToken(callback?: Function) {

    const accessTokenRequest = {
      scopes: ['api://' + environment.backendId + '/invoke'],
      account: this.msalService?.instance.getAllAccounts()[0],
    };

    this.msalService?.acquireTokenSilent(accessTokenRequest).subscribe((accessTokenReponse) => {
      if (accessTokenReponse != null) {
        // Acquire token silent success
        let accessToken = accessTokenReponse.accessToken;
        // Call your API with token
        if (callback) {
          callback(accessToken);
        }
      }
    })

  }

  getToken() {
    const accessTokenRequest = {
      scopes: ['api://' + environment.backendId + '/invoke'],
      account: this.msalService?.instance.getAllAccounts()[0],
    };

    return this.msalService?.acquireTokenSilent(accessTokenRequest)
  }

  getLoggedUser() {
    return this.userLogged;
  }

  detectMobile() {
    const toMatch = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i
    ];

    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  detectIpad() {
    const toMatch = [
      /iPad/i,
      /Mac/i,
    ];
    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  public setUser(account: any) {
    this.userLogged = account;
  }

  openUploadDialog(handler: Function) {
    const dialogRef = this.dialog.open(UploadDialogComponent, {
      data: {
      },
      width: '35em',
      height: '38em',
      backdropClass: 'bdrop'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        handler(result);
      }
    });
  }

  openDeleteDialog(title: string, message: string, data: any, handler: Function) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      data: {
        title: title,
        message: message,
        data
      },
      width: '30em',
      height: '20em',
      backdropClass: 'bdrop'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        handler();
      }
    });
  }

  openSnackBar(message: string, isError: boolean) {
    this.snackBar.openFromComponent(BulgariSnackbarComponent, {
      duration: 10000,
      horizontalPosition: 'left',
      panelClass: ['bulgari-snackbar'],
      data: {
        isError: isError,
        message: message
      }
    });
  }

  // * EVENT
  saveEvent(event: EventDTO) {
    localStorage.setItem('event', JSON.stringify(event));
    this._event = event;
  }

  getEvent(): EventDTO | undefined {
    if (localStorage.getItem('event')) {
      return JSON.parse(localStorage.getItem('event')!);
    };
    return undefined;
  }

  eraseEvent() {
    localStorage.removeItem('event');
  }

  initDateEvent() {
    if (this.event) {
      if (moment(new Date()) < moment(this.event.initDate)) {
        return moment(this.event.initDate).toDate();
      } else if (moment(new Date()) > moment(this.event.endDate)) {
        return moment(this.event.endDate).toDate();
      } else {
        return moment(new Date()).toDate();
      }
    }
    return moment(new Date()).toDate();
  }

  setRoleUser(role: RoleEnum) {
    this._userRole = role;
    this.roleListener.next(role);
  }

  isUserRole(roleType: RoleEnum) {
    return this.userRole === roleType;
  }

  getMinDateEvent(withOffset?: boolean) {
    let date = moment(this.event.initDate);
    if (withOffset) {
      date = date.subtract(this.EVENT_DAY_OFFSET, 'd')
    }
    return date.format('YYYY-MM-DD');
  }

  getMinDateEventMonth() {
    return moment(this.event.initDate).format('YYYY-MM');
  }

  getMaxDateEvent(withOffset?: boolean) {
    let date = moment(this.event.endDate);
    if (withOffset) {
      date = date.add(this.EVENT_DAY_OFFSET, 'd')
    }
    return date.format('YYYY-MM-DD');
  }

  isDayDuringTheEvent(date: Date, withOffset?: boolean): boolean {
    let eventInitDate = moment(this.getMinDateEvent(withOffset));
    let eventEndDate = moment(this.getMaxDateEvent(withOffset));
    let isDuringTheEvent = moment(date).isAfter(eventInitDate.startOf('d')) && moment(date).isBefore(eventEndDate.endOf('d'));
    return isDuringTheEvent;
  }

  getDayInEventRange(date: Date, withOffset?: boolean) {
    if (moment(date).isAfter(moment(this.getMaxDateEvent(withOffset)))) {
      return this.getMaxDateEvent(withOffset);
    }
    if (moment(date).isBefore(moment(this.getMinDateEvent(withOffset)))) {
      return this.getMinDateEvent(withOffset);
    }
    return date;
  }
  // * END EVENT

  base64ToArrayBuffer(base64: string) {
    let binary_string = window.atob(base64);
    let len = binary_string.length;
    let bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  toBase64(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result)
      };
      reader.onerror = reject;
    });
  }

  // * FILE MANAGEMENT
  saveFile(blob: Blob, filename: string) {
    const nav = (window.navigator as any);
    //? IE doesn't allow using a blob object directly as link href; instead it is necessary to use msSaveOrOpenBlob
    if (nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(blob, filename);
    } else {
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100)
    }
  }

  downloadFromUrl(url: string, fileName: string) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', url.replaceAll('"', ''));
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // * END FILE MANAGEMENT

  translateStatusText(status: ProductStatus) {
    switch (status) {
      case ProductStatus.NOT_AVAILABLE:
        return this.translateService.instant('PAGES.FILTER.NOT_AVAILABLE');
      case ProductStatus.AVAILABLE:
        return this.translateService.instant('PAGES.FILTER.AVAILABLE');
      case ProductStatus.IN_APPOINTMENT:
        return this.translateService.instant('PAGES.FILTER.IN_APPOINTMENT');
      case ProductStatus.UNLOCK:
        return this.translateService.instant('PAGES.FILTER.UNLOCK');
      case ProductStatus.BOOKED_FOR_SALES:
        return this.translateService.instant('PAGES.FILTER.BOOKED_FOR_SALES');
      case ProductStatus.BROKEN:
        return this.translateService.instant('PAGES.FILTER.BROKEN');
      case ProductStatus.WEAR:
        return this.translateService.instant('PAGES.FILTER.WEAR');
      case ProductStatus.CLIENT_VIEW:
        return this.translateService.instant('PAGES.FILTER.CLIENT_VIEW');
      case ProductStatus.UNDER_NEGOTIATION:
        return this.translateService.instant('PAGES.FILTER.UNDER_NEGOTIATION');

      default:
        return '';
    }
  }

  translateAppointmentStatus(appointmentStatus: AppointmentStatus) {
    switch (appointmentStatus) {
      case AppointmentStatus.CREATED:
        return this.translateService.instant("PAGES.MANAGEMENT.APPOINTMENT_STATUS.CREATED");
      case AppointmentStatus.OPEN:
        return this.translateService.instant("PAGES.MANAGEMENT.APPOINTMENT_STATUS.OPEN");
      case AppointmentStatus.CLOSED:
        return this.translateService.instant("PAGES.MANAGEMENT.APPOINTMENT_STATUS.CLOSED");
      case AppointmentStatus.DONE:
        return this.translateService.instant("PAGES.MANAGEMENT.APPOINTMENT_STATUS.DONE");
      case AppointmentStatus.END:
        return this.translateService.instant("PAGES.MANAGEMENT.APPOINTMENT_STATUS.END");
      default:
        return undefined;
    }
  }

  public scrollTop(id = "route-scroller") {
    let div = document.getElementById(id);
    if (div) {
      div.scrollTop = 0;
    }
  }

  fileExport(params: FileExportPayload, callbackSuccess?: Function, callbackError?: Function) {
    const { section, format, event, parameters } = params;
    let sectionLabel = section.split("_").map(str => this.capitalizeFirstLetter(str)).join("");
    let eventLabel = event.title.split(" ").map(str => this.capitalizeFirstLetter(str)).join("");
    let dateTimeLabel = moment().format('MMDDYYYY_HHmm');
    let isXlsx = format === ExportFormat.XLSX;
    let isPpt = format === ExportFormat.PPT;
    let downloadableViaBlob = isXlsx;
    const fileName = `MyEvent_${sectionLabel}_${eventLabel}_${dateTimeLabel}`.replaceAll(".", "");
    let apiCall = null;
    switch (section) {
      case Section.Dashboard: {
        apiCall = this.dashboardService.getExport(event.id);
        break;
      }
      case Section.BookedForSales: {
        apiCall = this.dashboardService.getExportBookForSale(event.id);
        break;
      }
      case Section.Agenda: {
        apiCall = this.managementService.getExport(event.id);
        break;
      }
      case Section.Allocation: {
        let { ShowCases, withImages } = parameters;
        apiCall = this.allocationService.getExport(event.id, { ShowCases }, isPpt, withImages);
        break;
      }
      case Section.Creations: {
        let { withImages } = parameters;
        apiCall = this.logisticService.getExport(event.id, { ...parameters });
        break;
      }
      case Section.Tray: {
        //A prescindere dal formato, gli export dei Tray ed ExtraTray viene fatto solo tramite URL
        downloadableViaBlob = true;
        let { Trays, ReferenceDate, Search, SapCodes, ShowcaseCodes } = parameters;
        apiCall = this.traysService.export(event.id, { Trays, ReferenceDate, Search, SapCodes, ShowcaseCodes }, isPpt);
        break;
      }
      case Section.ExtraTrays: {
        //A prescindere dal formato, gli export dei Tray ed ExtraTray viene fatto solo tramite URL
        downloadableViaBlob = true;
        let { Trays, ReferenceDate, Search, SapCodes, ShowcaseCodes } = parameters;
        apiCall = this.traysService.export(event.id, { Trays, ReferenceDate, Search, SapCodes, ShowcaseCodes }, isPpt, true);
        break;
      }
      case Section.Activity: {
        apiCall = this.traysService.exportActivities(event.id, { ...parameters });
        break;
      }
      case Section.AppointmentAnalytics: {
        apiCall = this.analyticsService.export(event.id, true)
        break;
      }
      case Section.ProductsAnalytics: {
        apiCall = this.analyticsService.export(event.id)
        break;
      }
      case Section.Finance: {
        apiCall = this.financeService.fullExport(event.id);
        break;
      }
    }
    if (apiCall) {
      // let exportStatus = new ExportStatus(section, fileName, format);
      // this.exportingLoader.next(exportStatus);
      apiCall.subscribe(res => {
        if (downloadableViaBlob) {
          this.saveFile(res, fileName);
        } else {
          this.downloadFromUrl(res, fileName);
        }
        // exportStatus.end();
        // this.exportingLoader.next(exportStatus);
        if (callbackSuccess) {
          callbackSuccess();
        }
        this.openSnackBar(
          this.translateService.instant('EXPORT_SUCCESS'), false);
      },
        err => {
          this.openSnackBar(this.translateService.instant('ERROR_GENERIC_RETRY'), true);
          // exportStatus.end();
          // this.exportingLoader.next(exportStatus);
          if (callbackError) {
            callbackError();
          }
          this.openSnackBar(
            this.translateService.instant(err.statusText || 'ERROR_GENERIC_RETRY'), true);
        });
    }
  }

  capitalizeFirstLetter(str: string): string {
    if (str.length === 0) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  goToNotAuthorizedPage(from?: string) {
    if (from) {
      console.log("goToNotAuthorizedPage - Redirect from:", from)
    }
    this.router.navigateByUrl('/not-authorized');
  }
  goToEventPage(from?: String) {
    if (from) {
      console.log("goToEventPage - Redirect from:", from)
    }
    this.router.navigateByUrl('/event');
  }
  goToLoginPage() {
    this.router.navigateByUrl('/login');
  }

  checkRoleAndAuthorization(routeName: RouteName): boolean | Observable<boolean> {
    let role = this.userRole;
    let dashboardAuthorizedRoles: RoleEnum[] = [];
    let index = -1;
    switch (routeName) {
      case RouteName.DASHBOARD: {
        index = 0;
        break;
      }
      case RouteName.MANAGEMENT: {
        index = 1;
        break;
      }
      case RouteName.ALLOCATION: {
        index = 2;
        break;
      }
      case RouteName.PRODUCTS: {
        index = 3;
        break;
      }
      case RouteName.ANALYTICS: {
        index = 4;
        break;
      }
      case RouteName.FINANCE: {
        index = 5;
        break;
      }
    }
    if (index >= 0 && ROUTES[index]) {
      dashboardAuthorizedRoles = ROUTES[index].authorizedRoles;
    }
    if (role !== undefined) {
      let authorized = dashboardAuthorizedRoles.includes(role!);
      if (authorized) {
        return true;
      }
      this.goToNotAuthorizedPage('shared.service.ts - checkRoleAndAuthorization() (role !== undefined)');
      return false;
    } else {
      //Richiamo il ruolo
      return this.dashboardService.getRole().pipe(
        tap(role => {
          this.setRoleUser(role);
          return of(true);
        }),
        catchError((e) => {
          this.goToNotAuthorizedPage('shared.service.ts - getRole().error');
          return of(false);
        }),
      )
    }
  }
}
